import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

interface TransactionFormProps {
  lenderId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const TransactionForm = ({
  lenderId,
  onSuccess,
  onCancel,
}: TransactionFormProps) => {
  const [formData, setFormData] = useState({
    transaction_date: new Date().toISOString().split("T")[0],
    amount: "",
    transaction_type: "loan_received",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await supabase.from("lender_transactions").insert({
        lender_profile_id: lenderId,
        transaction_date: formData.transaction_date,
        amount: parseFloat(formData.amount),
        transaction_type: formData.transaction_type,
        notes: formData.notes || null,
      });

      if (error) throw error;

      toast({ title: "Transaction added successfully" });
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="transaction_date">Transaction Date *</Label>
        <Input
          id="transaction_date"
          type="date"
          value={formData.transaction_date}
          onChange={(e) =>
            setFormData({ ...formData, transaction_date: e.target.value })
          }
          required
        />
      </div>

      <div>
        <Label htmlFor="amount">Amount ($) *</Label>
        <Input
          id="amount"
          type="number"
          step="0.01"
          min="0"
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          required
        />
      </div>

      <div>
        <Label htmlFor="transaction_type">Transaction Type *</Label>
        <Select
          value={formData.transaction_type}
          onValueChange={(value) =>
            setFormData({ ...formData, transaction_type: value })
          }
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="loan_received">Loan Received</SelectItem>
            <SelectItem value="interest_payment">Interest Payment</SelectItem>
            <SelectItem value="principal_repayment">
              Principal Repayment
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          rows={2}
          placeholder="Optional transaction notes..."
        />
      </div>

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? "Adding..." : "Add Transaction"}
        </Button>
      </div>
    </form>
  );
};
